import { showToast } from '@lokalise/louis';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type { SagaIterator } from 'redux-saga';
import { put, select, takeEvery } from 'redux-saga/effects';

import {
    getServiceProviderPublicCertificate,
    saveSecuritySettings,
} from '@app/features/project/pages/ProjectsList/api';

import * as actions from './actions';
import * as selectors from './selectors';
import type { AdvancedSecurityForm } from './types';

// these two are temp functions before keys aren't changed on backend
const toSnakeCase = (str: string) => str.replace(/([A-Z])/g, '_$1').toLowerCase();
const mapFormData = (formData: AdvancedSecurityForm) => {
    const tempKeys = [
        'requireTwoFactorAuth',
        'ssoEnabled',
        'ssoRequired',
        'ssoDomain',
        'ssoSamlIdpEndpointUrl',
        'ssoSamlIdpIssuer',
        'ssoSamlIdpCertificate',
        'ssoSamlStrict',
    ];
    const result: Record<string, unknown> = {};
    Object.keys(formData).forEach((key) => {
        if (tempKeys.includes(key)) {
            result[toSnakeCase(key)] = formData[key as keyof AdvancedSecurityForm];
        } else {
            result[key] = formData[key as keyof AdvancedSecurityForm];
        }
    });

    return result;
};

function* generateCertificate({ payload: { spCertificate } }: ReturnType<typeof actions.generateCertificate>) {
    yield put(actions.setSpCertificate(spCertificate));
    try {
        const formData: AdvancedSecurityForm = yield select(selectors.getFormData);
        if (!formData.ssoSamlStrict) return;
        const response: AxiosResponse<string> = yield getServiceProviderPublicCertificate(formData);
        yield put(actions.setSpCertificate(response.data));
    } catch (e) {
        let message = 'Something went wrong, please contact support via chat or hello@lokalise.com';

        if (axios.isAxiosError(e)) {
            message = e.response?.data?.error?.message;
        }

        yield actions.setErrorMessage(message);
    }
}

function* submitForm() {
    yield put(actions.setSubmitting(true));
    try {
        const formData: AdvancedSecurityForm = yield select(selectors.getFormData);
        yield saveSecuritySettings(mapFormData(formData));
        showToast({ type: 'success', title: 'Advanced security settings have been successfully saved' });
    } catch (e) {
        let message = 'Something went wrong, please contact support via chat or hello@lokalise.com';

        if (axios.isAxiosError(e)) {
            message = e.response?.data?.error?.message;
        }

        yield actions.setErrorMessage(message);
        showToast({ type: 'error', title: message });
    } finally {
        yield put(actions.setSubmitting(false));
    }
}

export default function* initAdvancedSecuritySagas(): SagaIterator {
    yield takeEvery(actions.generateCertificate.match, generateCertificate);
    yield takeEvery(actions.submitForm.match, submitForm);
}
